
import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { analyzeImage } from '@/utils/imageAnalysis';
import 'react-image-crop/dist/ReactCrop.css';

interface ImageCropperProps {
  src: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  onCancel: () => void;
  aspectRatio?: number;
  presetRatios?: { label: string; ratio: number }[];
}

const DEFAULT_PRESETS = [
  { label: 'Auto (Smart)', ratio: 0 }, // Special case for auto-detection
  { label: 'Service Card (4:3)', ratio: 4/3 },
  { label: 'Portrait (3:4)', ratio: 3/4 },
  { label: 'Square (1:1)', ratio: 1 },
  { label: 'Wide (16:9)', ratio: 16/9 }
];

export const ImageCropper: React.FC<ImageCropperProps> = ({
  src,
  onCropComplete,
  onCancel,
  aspectRatio = 4/3,
  presetRatios = DEFAULT_PRESETS
}) => {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [selectedRatio, setSelectedRatio] = useState(aspectRatio);
  const [smartRatio, setSmartRatio] = useState<number | null>(null);
  const [imageAnalysis, setImageAnalysis] = useState<any>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    
    // Analyze the image for smart suggestions
    const analysis = analyzeImage(e.currentTarget);
    setImageAnalysis(analysis);
    setSmartRatio(analysis.recommendedCardAspectRatio);
    
    // Use smart ratio if auto is selected, otherwise use selected ratio
    const effectiveRatio = selectedRatio === 0 ? analysis.recommendedCardAspectRatio : selectedRatio;
    
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        effectiveRatio,
        width,
        height,
      ),
      width,
      height,
    );
    setCrop(crop);
  }, [selectedRatio]);

  const handleAspectRatioChange = (newRatio: number) => {
    setSelectedRatio(newRatio);
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      // Use smart ratio if auto (0) is selected
      const effectiveRatio = newRatio === 0 ? (smartRatio || 4/3) : newRatio;
      
      const newCrop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 90,
          },
          effectiveRatio,
          width,
          height,
        ),
        width,
        height,
      );
      setCrop(newCrop);
    }
  };

  const getCroppedImage = useCallback(async () => {
    if (!completedCrop || !imgRef.current || !previewCanvasRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No 2d context');
    }

    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY,
    );

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        0.9
      );
    });
  }, [completedCrop]);

  const handleCropComplete = async () => {
    try {
      const croppedImageBlob = await getCroppedImage();
      if (croppedImageBlob) {
        onCropComplete(croppedImageBlob);
      }
    } catch (error) {
      console.error('Error cropping image:', error);
    }
  };

  // Get effective ratio for display
  const getEffectiveRatio = () => {
    return selectedRatio === 0 ? (smartRatio || 4/3) : selectedRatio;
  };

  return (
    <Card className="w-full max-w-4xl">
      <CardHeader>
        <CardTitle>Crop Your Service Image</CardTitle>
        {imageAnalysis && (
          <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
            <p className="font-medium">📸 Smart Analysis:</p>
            <p>• Image Type: <span className="font-medium capitalize">{imageAnalysis.cardType}</span></p>
            <p>• Aspect Ratio: <span className="font-medium">{imageAnalysis.aspectRatio.toFixed(2)}</span></p>
            <p>• Recommended Card: <span className="font-medium">{imageAnalysis.recommendedCardAspectRatio.toFixed(2)}</span></p>
            {imageAnalysis.cardType === 'portrait' && (
              <p className="text-blue-600 mt-1">💡 Portrait detected! Consider using "Auto (Smart)" or "Portrait (3:4)" for best results.</p>
            )}
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {presetRatios.map((preset) => (
            <Badge
              key={preset.label}
              variant={selectedRatio === preset.ratio ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => handleAspectRatioChange(preset.ratio)}
            >
              {preset.label}
              {preset.ratio === 0 && smartRatio && ` (${smartRatio.toFixed(2)})`}
            </Badge>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={getEffectiveRatio()}
              className="max-w-full"
            >
              <img
                ref={imgRef}
                alt="Crop me"
                src={src}
                onLoad={onImageLoad}
                className="max-w-full h-auto"
              />
            </ReactCrop>
          </div>
          
          {completedCrop && (
            <div className="w-full lg:w-80">
              <h3 className="text-sm font-medium mb-2">Preview</h3>
              <div className="bg-gray-100 rounded-lg p-4 space-y-4">
                {/* Regular Service Card Preview */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Standard Card (4:3)</p>
                  <div className="w-full aspect-[4/3] bg-white rounded-lg overflow-hidden shadow-sm">
                    <canvas
                      ref={previewCanvasRef}
                      className="w-full h-full object-cover"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                </div>
                
                {/* Adaptive Card Preview if different */}
                {imageAnalysis && imageAnalysis.cardType !== 'square' && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">
                      Adaptive Card ({imageAnalysis.cardType === 'portrait' ? '3:4' : '16:9'})
                    </p>
                    <div 
                      className="w-full bg-white rounded-lg overflow-hidden shadow-sm"
                      style={{ 
                        aspectRatio: imageAnalysis.recommendedCardAspectRatio,
                      }}
                    >
                      <canvas
                        className="w-full h-full object-cover"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleCropComplete} disabled={!completedCrop}>
            Use This Crop
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
